"use client";
import { css, Global as EmotionGlobal } from "@emotion/react";
import React from "react";
import { Global, Theme as ThemeUITheme, ThemeUIProvider } from "theme-ui";

import { IdProvider } from "../../hooks/useId";
import { OverlayContextProvider } from "../../hooks/useOverlay";
import { getFontFacesDeclaration } from "../Fonts";
import { IconNameType } from "../Icon";
import { IconProvider } from "../Icon/IconContext";
import {
  CornerRadii,
  Elevation,
  FontSize,
  FontWeight,
  LetterSpacing,
  LineHeight,
  ModernaBrand,
  Space,
  SystemColors
} from "../Theme";
import { ThemeContext } from "./ThemeContext";
import { setColorsForTheme, Theme, ThemeBrandColors, ThemeMode } from "./types";

export const resetClassName = "mdrna-reset";

const ResetStyles: React.FC = () => {
  const fontFacesDeclaration = React.useMemo(
    () => getFontFacesDeclaration(),
    []
  );
  return (
    <>
      <Global
        styles={{
          [`.${resetClassName}`]: {
            all: "unset",
            cursor: "revert",
            display: "revert",
            verticalAlign: "baseline"
          },
          "*": {
            WebkitTapHighlightColor: "transparent"
          }
        }}
      />
      <EmotionGlobal
        styles={css`
          ${fontFacesDeclaration}
        `}
      />
    </>
  );
};

export const getTheme = (themeBranding?: ThemeBrandColors) => {
  const space = new Array(192)
    .fill(null)
    .map((_value, i) => i * 4) as Theme["space"];

  const spaceType = {
    ...space,
    ...Space
  };

  return {
    breakpoints: ["720px", "1024px", "1440px", "1920px"] as [
      string,
      string,
      string,
      string
    ],
    colors: {
      ...SystemColors,
      ...setColorsForTheme(themeBranding || ModernaBrand)
    },
    space: spaceType,
    sizes: {
      ...spaceType,
      containerDesktopMargin: 64,
      containerDesktopMaxWidth: 1440,
      containerMobileMargin: 16,
      containerMobileMaxWidth: 748,
      containerTabletMargin: 40,
      containerTabletMaxWidth: 748,
      gridDesktopColumnGutter: 32,
      gridMobileColumnGutter: 16,
      gridTabletColumnGutter: 24
    },
    shadows: Elevation,
    radii: CornerRadii,
    fontSizes: FontSize,
    fontWeights: FontWeight,
    lineHeights: LineHeight,
    letterSpacings: LetterSpacing,
    fonts: {
      // TODO: refactor?
      body: "Aeonik",
      code: "RobotoMono"
    },
    grid: {
      // TODO: refactor?
      gridDesktopColumnCount: 12,
      gridMobileColumnCount: 4,
      gridTabletColumnCount: 8
    },
    config: {
      useCustomProperties: false,
      useLocalStorage: false
    },
    transitions: {
      "transition-color-medium":
        "background-color 100ms, border-color 100ms, color 100ms, opacity 100ms, box-shadow 100ms, outline-color 100ms, text-decoration-color 100ms",
      "transition-color-slow":
        "background-color 300ms, border-color 300ms, color 300ms, opacity 300ms, box-shadow 300ms, outline-color 300ms, text-decoration-color 300ms",
      "transition-ease-out-fast":
        "background-color 100ms, border-color 100ms, color 100ms, opacity 100ms, box-shadow 100ms, text-decoration-color 100ms, transform 300ms cubic-bezier(0.4, 0, 0, 1)",
      "transition-ease-out-medium":
        "background-color 200ms, border-color 200ms, color 200ms, opacity 200ms, box-shadow 200ms, text-decoration-color 200ms, transform 400ms cubic-bezier(0.4, 0, 0.06, 1)",
      "transition-ease-out-slow":
        "background-color 400ms, border-color 400ms, color 400ms, opacity 400ms, box-shadow 400ms, text-decoration-color 400ms, transform 800ms cubic-bezier(0.4, 0, 0.06, 1)"
    },
    zIndices: {
      dropdown: 1200,
      modal: 1300,
      navbar: 1100,
      panel: 1150
    }
  };
};

export const ThemeProvider: React.FC<
  React.PropsWithChildren<{
    mode?: ThemeMode;
    themeBranding?: ThemeBrandColors;
    preCacheIcons?: IconNameType[];
  }>
> = ({
  children,
  mode = "light",
  themeBranding = ModernaBrand,
  preCacheIcons = []
}) => {
  const { initialized } = React.useContext(ThemeContext);

  const theme = getTheme(themeBranding);
  const themeContextNext = React.useMemo(
    () => ({
      initialized: true,
      mode,
      themeInitial: {
        ...theme
      }
    }),
    [mode, theme]
  );

  return initialized ? (
    <>{children}</>
  ) : (
    <IdProvider>
      <OverlayContextProvider>
        <ThemeContext.Provider value={themeContextNext}>
          <ThemeUIProvider theme={theme as unknown as ThemeUITheme}>
            <IconProvider preCacheIcons={preCacheIcons}>
              <ResetStyles />
              {children}
            </IconProvider>
          </ThemeUIProvider>
        </ThemeContext.Provider>
      </OverlayContextProvider>
    </IdProvider>
  );
};
